import { useState } from "react";

export type Dachform =
  | "satteldach"
  | "walmdach"
  | "krueppelwalmdach"
  | "zeltdach"
  | "pultdach"
  | "flachdach"
  | "frei"
  | "frei_aufst"
  | "fassade";

interface DachformOption {
  id: Dachform;
  label: string;
  defaultTilt: number;
}

const DACHFORMEN: DachformOption[] = [
  { id: "satteldach",       label: "Satteldach",       defaultTilt: 35 },
  { id: "walmdach",         label: "Walmdach",          defaultTilt: 30 },
  { id: "krueppelwalmdach", label: "Krüppelwalmdach",  defaultTilt: 35 },
  { id: "zeltdach",         label: "Zeltdach",          defaultTilt: 40 },
  { id: "pultdach",         label: "Pultdach",          defaultTilt: 15 },
  { id: "flachdach",        label: "Flachdach",         defaultTilt: 5  },
  { id: "frei",             label: "Frei",              defaultTilt: 30 },
  { id: "frei_aufst",       label: "Frei (Aufst.)",     defaultTilt: 30 },
  { id: "fassade",          label: "Fassade",           defaultTilt: 90 },
];

interface Props {
  value: Dachform;
  onChange: (form: Dachform, defaultTilt: number) => void;
}

export function DachformSelector({ value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const selected = DACHFORMEN.find((d) => d.id === value);

  return (
    <div>
      {/* Kompakte Kopfzeile: zeigt die gewählte Dachform; Icons erst auf Klick */}
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          borderRadius: 6,
          border: "1px solid #e5e7eb",
          background: "#fff",
          cursor: "pointer",
          fontSize: 12,
          color: "#374151",
        }}
      >
        <span style={{ fontWeight: 600 }}>Dachform:</span>
        <span style={{ color: "#1d4ed8", fontWeight: 600 }}>{selected?.label ?? "—"}</span>
        <span style={{ color: "#9ca3af" }}>{open ? "▴ schließen" : "▾ ändern"}</span>
      </button>

      {open && (
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8 }}>
          {DACHFORMEN.map((d) => (
            <button
              key={d.id}
              onClick={() => { onChange(d.id, d.defaultTilt); setOpen(false); }}
              title={d.label}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                padding: "5px 4px",
                width: 56,
                borderRadius: 6,
                border: `2px solid ${value === d.id ? "#2563eb" : "#e5e7eb"}`,
                background: value === d.id ? "#eff6ff" : "#fff",
                cursor: "pointer",
              }}
            >
              <DachformIcon form={d.id} />
              <span
                style={{
                  fontSize: 9,
                  color: value === d.id ? "#1d4ed8" : "#6b7280",
                  textAlign: "center",
                  lineHeight: 1.15,
                  fontWeight: value === d.id ? 600 : 400,
                }}
              >
                {d.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// SVG-Icons für jede Dachform — einfache geometrische Formen, kein externes Bild
function DachformIcon({ form }: { form: Dachform }) {
  const size = 36;
  switch (form) {
    case "satteldach":
      return (
        <svg width={size} height={size} viewBox="0 0 36 36">
          <rect x="2" y="18" width="32" height="14" fill="#94a3b8" rx="1" />
          <polygon points="18,4 2,18 34,18" fill="#2563eb" opacity="0.8" />
          <polygon points="18,4 34,18 26,18 26,14" fill="#1d4ed8" opacity="0.9" />
        </svg>
      );
    case "walmdach":
      return (
        <svg width={size} height={size} viewBox="0 0 36 36">
          <rect x="2" y="20" width="32" height="12" fill="#94a3b8" rx="1" />
          <polygon points="18,4 6,20 30,20" fill="#2563eb" opacity="0.8" />
          <polygon points="2,20 6,20 18,4 30,20 34,20 18,6" fill="#1d4ed8" opacity="0.5" />
          <polygon points="18,4 30,20 34,20" fill="#3b82f6" opacity="0.7" />
        </svg>
      );
    case "pultdach":
      return (
        <svg width={size} height={size} viewBox="0 0 36 36">
          <rect x="2" y="22" width="32" height="10" fill="#94a3b8" rx="1" />
          <polygon points="2,22 34,10 34,22" fill="#2563eb" opacity="0.8" />
        </svg>
      );
    case "flachdach":
      return (
        <svg width={size} height={size} viewBox="0 0 36 36">
          <rect x="2" y="14" width="32" height="4" fill="#2563eb" opacity="0.8" rx="1" />
          <rect x="2" y="18" width="32" height="12" fill="#94a3b8" rx="1" />
        </svg>
      );
    case "zeltdach":
      return (
        <svg width={size} height={size} viewBox="0 0 36 36">
          <rect x="2" y="22" width="32" height="10" fill="#94a3b8" rx="1" />
          <polygon points="18,4 2,22 34,22" fill="#2563eb" opacity="0.8" />
          <polygon points="18,4 34,22 28,22" fill="#1d4ed8" opacity="0.6" />
        </svg>
      );
    case "krueppelwalmdach":
      return (
        <svg width={size} height={size} viewBox="0 0 36 36">
          <rect x="2" y="20" width="32" height="12" fill="#94a3b8" rx="1" />
          <polygon points="10,10 26,10 34,20 2,20" fill="#2563eb" opacity="0.7" />
          <polygon points="10,10 18,4 26,10" fill="#1d4ed8" opacity="0.9" />
        </svg>
      );
    case "frei":
      return (
        <svg width={size} height={size} viewBox="0 0 36 36">
          <polygon
            points="4,30 18,6 32,30"
            fill="none"
            stroke="#2563eb"
            strokeWidth="2"
            strokeDasharray="3,2"
          />
          <circle cx="4" cy="30" r="2.5" fill="#2563eb" />
          <circle cx="18" cy="6" r="2.5" fill="#2563eb" />
          <circle cx="32" cy="30" r="2.5" fill="#2563eb" />
        </svg>
      );
    case "frei_aufst":
      return (
        <svg width={size} height={size} viewBox="0 0 36 36">
          <line x1="6" y1="30" x2="6" y2="20" stroke="#94a3b8" strokeWidth="2" />
          <line x1="30" y1="30" x2="30" y2="16" stroke="#94a3b8" strokeWidth="2" />
          <polygon points="4,20 32,16 32,20 4,24" fill="#2563eb" opacity="0.8" />
          <line x1="4" y1="30" x2="32" y2="30" stroke="#64748b" strokeWidth="2" />
        </svg>
      );
    case "fassade":
      return (
        <svg width={size} height={size} viewBox="0 0 36 36">
          <rect x="8" y="4" width="4" height="28" fill="#2563eb" opacity="0.8" />
          <rect x="12" y="4" width="16" height="28" fill="#94a3b8" rx="1" />
          <rect x="8" y="4" width="20" height="4" fill="#1d4ed8" opacity="0.5" />
        </svg>
      );
    default:
      return (
        <svg width={size} height={size} viewBox="0 0 36 36">
          <rect x="2" y="2" width="32" height="32" fill="#e5e7eb" rx="4" />
        </svg>
      );
  }
}
